import { useCallback } from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { AnswerID } from '@aytm/app/swidget/base/questions/types'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import { unescapeTags } from '@aytm/lib/common/func'
import { EcommerceBannerPlacement, IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { useChildrenProducts } from '../../hooks/useChildrenProducts.tsx'
import { useBannerImpression } from '../../hooks/useBannerImpression.ts'
import { hiddenClass } from '../../utils/hiddenClass.ts'
import AddToCartButton from './AddToCartButton.tsx'
import Badges from './Badges.tsx'
import Rating from './Rating.tsx'
import ClubcardPrice from './ClubcardPrice.tsx'
import AldiPriceMatch from './AldiPriceMatch.tsx'
import style from './ProductCard.module.scss'
import favouriteIcon from '../assets/icons/favourite.svg'

interface IProductCardProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  amount: number
  disabled?: boolean
  bannerPlacement?: EcommerceBannerPlacement
  registerCardRef?: (id: AnswerID, element: HTMLDivElement | null) => void
  onSelect: () => void
  onAdd: (qnt: number) => void
}

const ProductCard = ({
  schema,
  answer,
  amount,
  disabled = false,
  bannerPlacement,
  registerCardRef,
  onSelect,
  onAdd
}: IProductCardProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  // A rating below one star is unreachable for a real product, so it is treated as no rating.
  const hasRating = Number(answer.rating) >= 1
  const hasVariants = useChildrenProducts({ answer, schema, excludeCurrentProduct: true }).length > 0
  const bannerRef = useBannerImpression(answer.id, bannerPlacement ?? null)
  const setCardRef = useCallback(
    (element: HTMLDivElement | null) => registerCardRef?.(answer.id, element),
    [answer.id, registerCardRef]
  )

  const badges = <Badges answer={answer} className={style.badges} />

  const image = answer.image ? (
    <div
      className={style.image}
      role="img"
      aria-label={answer.image.alt_text || t('questions', 'img', 'noAlt')}
      style={{ backgroundImage: `url('${getMediumAssetPath(answer.image)}')` }}
    />
  ) : (
    <div className={style.noImage} />
  )

  const rating = hasRating && (
    <div className={style.ratingRow}>
      <span onClick={event => event.stopPropagation()}>
        <EmptyLink color="#00539f">
          <Rating rating={answer.rating} />
        </EmptyLink>
      </span>
      <span className={style.ratingText}>
        {Number(answer.rating).toFixed(1)}
        {answer.reviews != null && ` (${answer.reviews.toLocaleString(languageTag)})`}
      </span>
    </div>
  )

  const favourite = (
    <span className={style.favourite} onClick={event => event.stopPropagation()}>
      <EmptyLink color="#00539f">
        <img
          src={favouriteIcon}
          alt={t('questions', 'ec', 'tesco', 'addToFavourites')}
          className={style.favouriteIcon}
        />
      </EmptyLink>
    </span>
  )

  const sponsored = answer.sponsored && (
    <div className={style.sponsored}>{t('questions', 'ec', 'tesco', 'sponsored')}</div>
  )
  const title = <div className={style.title} dangerouslySetInnerHTML={{ __html: answer.text }} />

  const moreLikeThis = hasVariants && (
    <span className={style.moreLikeThis}>
      {t('questions', 'ec', 'tesco', 'moreLikeThis')}
      <i className={`pi pi-angle-right ${style.moreLikeThisChevron}`} />
    </span>
  )

  const importantInfo = answer.best_seller && (
    <span onClick={event => event.stopPropagation()}>
      <EmptyLink color="#00539f">
        <span className={style.importantInfo}>
          <span className={style.infoIcon}>i</span>
          <span className={style.importantInfoText}>{t('questions', 'ec', 'tesco', 'importantInformation')}</span>
        </span>
      </EmptyLink>
    </span>
  )

  const offers = (
    <div className={style.offers}>
      <ClubcardPrice schema={schema} answer={answer} />
      <AldiPriceMatch answer={answer} />
    </div>
  )

  const footer = (
    <div className={style.footer}>
      <div className={style.prices}>
        <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
        {answer.per_count_price && (
          <span
            className={style.unitPrice}
            dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }}
          />
        )}
      </div>
      <AddToCartButton amount={amount} limit={schema.max_qty_per_product} disabled={disabled} onAdd={onAdd} />
    </div>
  )

  // The app's carousel card is the photo, the title and the buy line; labels and offers stay on the page.
  if (viewport.mobile && bannerPlacement) {
    return (
      <div
        ref={bannerRef}
        data-testid="SW-EC-Item"
        aria-disabled={disabled}
        className={`${style.carouselMobile} ${hiddenClass(answer)} ${disabled ? style.disabled : ''}`}
        onClick={onSelect}
      >
        <div className={style.imageWrapper}>{image}</div>
        {title}
        {footer}
      </div>
    )
  }

  // The app's results are rows: photo on the left, the details beside it, the offers across the full width.
  // Carousel cards keep the column card on mobile too.
  if (viewport.mobile && !bannerPlacement) {
    return (
      <div
        ref={setCardRef}
        data-testid="SW-EC-Item"
        aria-disabled={disabled}
        className={`${style.cardMobile} ${hiddenClass(answer)} ${disabled ? style.disabled : ''}`}
        onClick={onSelect}
      >
        {badges}
        {favourite}
        <div className={style.row}>
          <div className={style.imageWrapper}>{image}</div>
          <div className={style.body}>
            {sponsored}
            {title}
            {rating}
            {footer}
            {moreLikeThis}
            {importantInfo}
          </div>
        </div>
        {offers}
      </div>
    )
  }

  return (
    <div
      ref={bannerPlacement ? bannerRef : setCardRef}
      data-testid="SW-EC-Item"
      aria-disabled={disabled}
      className={`${style.card} ${hiddenClass(answer)} ${bannerPlacement ? style.compact : ''} ${disabled ? style.disabled : ''}`}
      onClick={onSelect}
    >
      {badges}
      {favourite}
      <div className={style.imageWrapper}>{image}</div>

      <div className={style.body}>
        {sponsored}
        {title}
        {rating}
        {moreLikeThis}
        {importantInfo}
      </div>

      {offers}
      {footer}
    </div>
  )
}

export default ProductCard
